/**
 * One download path for both platforms.
 *
 * In a browser there is no choice: fetch the response, hold it in memory, hand a
 * blob URL to an anchor. That is fine for a photo and survivable for a small ZIP.
 *
 * On desktop it is not. A full-event RAW archive is routinely several gigabytes,
 * and buffering one in the renderer kills the tab before the download finishes.
 * The desktop path streams the response to a file the user picks, so the size
 * stops mattering — and the save dialog, progress, and "reveal in folder" come
 * from the OS rather than being reimplemented.
 *
 * `onProgress({ received, total, pct })` is called on both platforms.
 */

export function isDesktop() {
	return Boolean(typeof window !== "undefined" && window.photentra?.isDesktop);
}

export async function downloadFromApi({
	url,
	filename,
	method = "GET",
	headers = {},
	body,
	onProgress,
}) {
	if (isDesktop()) {
		return desktopDownload({ url, filename, method, headers, body, onProgress });
	}
	return browserDownload({ url, filename, method, headers, body, onProgress });
}

async function desktopDownload({ url, filename, method, headers, body, onProgress }) {
	// The main process reports bytes as it writes them; unsubscribe as soon as
	// this download resolves so a second one doesn't inherit the callback.
	const unsubscribe = onProgress
		? window.photentra.onDownload((event) => {
				if (event.kind !== "progress") return;
				onProgress({
					received: event.received,
					total: event.total,
					pct: event.total ? Math.min(99, Math.round((event.received / event.total) * 100)) : null,
				});
			})
		: null;

	try {
		const result = await window.photentra.downloadFile({
			url,
			method,
			headers,
			body,
			suggestedName: filename,
		});

		if (result?.ok && onProgress) onProgress({ received: 0, total: 0, pct: 100 });
		// A cancelled save dialog is a normal outcome, not a failure to report.
		if (!result?.ok && !result?.cancelled) {
			throw new Error(result?.error || "Download failed");
		}
		return result;
	} finally {
		if (unsubscribe) unsubscribe();
	}
}

async function browserDownload({ url, filename, method, headers, body, onProgress }) {
	const response = await fetch(url, { method, headers, body });
	if (!response.ok) throw new Error("Download failed");

	// A streamed ZIP has no Content-Length; the API sends its own estimate so the
	// bar shows something truthful on a multi-gigabyte archive.
	const estimated =
		Number(response.headers?.get?.("Content-Length")) ||
		Number(response.headers?.get?.("X-Zip-Estimated-Bytes")) ||
		0;

	let blob;
	if (onProgress && response.body?.getReader) {
		const reader = response.body.getReader();
		const chunks = [];
		let received = 0;
		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;
			chunks.push(value);
			received += value.length;
			onProgress({
				received,
				total: estimated,
				pct: estimated ? Math.min(99, Math.round((received / estimated) * 100)) : null,
			});
		}
		blob = new Blob(chunks);
	} else {
		blob = await response.blob();
	}

	if (onProgress) onProgress({ received: blob.size, total: blob.size, pct: 100 });

	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = resolveFilename(response, filename);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(objectUrl);

	return { ok: true };
}

/** The server's Content-Disposition wins when it sends one. */
function resolveFilename(response, fallback) {
	const disposition = response.headers?.get?.("Content-Disposition");
	const match = disposition?.match(/filename="?([^"]+)"?/);
	return match ? match[1] : fallback;
}
